import React, { useCallback, useRef, useState } from 'react';
import { HERO } from './landingCopy';
import { HERO_SCENES } from './landingAssets';
import HeroIntroMark from './HeroIntroMark';

const prefersReducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Hero "Drieluik": centered proposition, the one-time Zoea mark intro, three real scenes in identical frames.
// Below 900px the scenes become a scroll-snap row with accessible pillar navigation.
const HeroSection = () => {
  const [current, setCurrent] = useState(0);
  const trip = useRef(null);
  const scenes = useRef([]);
  const frame = useRef(0);
  const last = HERO.scenes.length - 1;

  const offsetOf = useCallback((index) => {
    const row = trip.current;
    const scene = scenes.current[index];
    const padding = parseFloat(window.getComputedStyle(row).paddingLeft) || 0;
    return scene.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft - padding;
  }, []);

  const go = (index) => {
    const next = Math.max(0, Math.min(last, index));
    trip.current?.scrollTo?.({ left: offsetOf(next), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    setCurrent(next);
  };

  // The active pillar follows the scroll position (swipe, keys or buttons).
  const onScroll = () => {
    window.cancelAnimationFrame(frame.current);
    frame.current = window.requestAnimationFrame(() => {
      if (!trip.current) return;
      const left = trip.current.scrollLeft;
      let best = 0;
      let distance = Infinity;
      HERO.scenes.forEach((_, index) => {
        const d = Math.abs(offsetOf(index) - left);
        if (d < distance) {
          distance = d;
          best = index;
        }
      });
      setCurrent(best);
    });
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      go(current + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      go(current - 1);
    }
  };

  return (
    <section className="zl-hero" id="top" aria-labelledby="hero-title">
      <div className="zl-wrap">
        <div className="zl-hero-head">
          <div className="zl-intro-row">
            <HeroIntroMark />
            <p className="zl-eyebrow" data-testid="hero-eyebrow">{HERO.eyebrow}</p>
          </div>
          <h1 id="hero-title" data-testid="hero-title">{HERO.title}</h1>
          <p className="zl-lead">{HERO.lead}</p>
          <div className="zl-ctas">
            <a className="zl-btn zl-btn-primary" data-testid="hero-primary-cta" href="#kennismaking">
              <span className="zl-dot" aria-hidden="true" />
              {HERO.primaryCta}
            </a>
            <a className="zl-linkbtn" href="#tour">{HERO.secondaryCta}</a>
          </div>
        </div>

        <div className="zl-trip-nav" data-testid="trip-nav" role="group" aria-label={HERO.tripLabel}>
          {HERO.scenes.map((scene, index) => (
            <button
              key={scene.key}
              type="button"
              aria-controls={scene.id}
              aria-current={current === index ? 'true' : 'false'}
              onClick={() => go(index)}
            >
              <i aria-hidden="true" />
              {scene.pillar}
            </button>
          ))}
        </div>

        <div className="zl-trip" ref={trip} tabIndex={0} role="region" aria-label={HERO.tripRegion} onScroll={onScroll} onKeyDown={onKeyDown}>
          {HERO.scenes.map((scene, index) => {
            const image = HERO_SCENES[scene.key];
            return (
              <figure key={scene.key} id={scene.id} ref={(node) => { scenes.current[index] = node; }} data-testid="hero-scene" data-capture-width="440">
                <figcaption>
                  <span className="zl-p-name"><i aria-hidden="true" />{scene.pillar}</span>
                  <span className="zl-p-what">{scene.what}</span>
                </figcaption>
                <div className="zl-frame">
                  <div className="zl-screen" data-testid="hero-scene-screen">
                    <img
                      src={image.src}
                      width={image.width}
                      height={image.height}
                      alt={image.alt}
                      loading="eager"
                      {...(index === 0 ? { fetchPriority: 'high' } : {})}
                    />
                  </div>
                  <p className="zl-src">{scene.source}</p>
                </div>
              </figure>
            );
          })}
        </div>

        <div className="zl-trip-next">
          <button type="button" onClick={() => go(current - 1)} disabled={current === 0}>{HERO.previous}</button>
          <span data-testid="trip-count" aria-live="polite">{current + 1} van {HERO.scenes.length}</span>
          <button type="button" onClick={() => go(current + 1)} disabled={current === last}>
            {current < last ? `Volgende: ${HERO.scenes[current + 1].pillar} →` : HERO.nextLast}
          </button>
        </div>
        <p className="zl-hero-foot"><span className="zl-demo-tag">{HERO.foot}</span></p>
      </div>
    </section>
  );
};

export default HeroSection;
